import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardDataV4 } from '../../../hooks/useDashboardDataV4';
import { AttentionItem } from '../../../types/dashboard-v4.types';

// Import all components
import { WelcomeHeader } from './components/WelcomeHeader';
import { ActivityFeed } from './components/ActivityFeed';
import { MetricsCards } from './components/MetricsCards';
import { SeverityDonutChart } from './components/SeverityDonutChart';
import { SignalsTimeChart } from './components/SignalsTimeChart';
import { ComplaintDetectionChart } from './components/ComplaintDetectionChart';
import { ChannelBreakdownChart } from './components/ChannelBreakdownChart';
import { VulnerabilityMetric } from './components/VulnerabilityMetric';
import { BriefingQuote } from './components/BriefingQuote';
import TodayDashboardSkeleton from './TodayDashboardSkeleton';

const TodayDashboardV4: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useDashboardDataV4();

  const handleAttentionItemClick = (item: AttentionItem) => {
    // Navigate to the signal page using the signalId
    const path = item.actionPath || `/v3/vault/${item.signalId}`;
    navigate(path);
  };

  if (loading) {
    return <TodayDashboardSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#FBF9F6]">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading dashboard data</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#4342FF] text-white rounded-lg hover:bg-[#3331CC]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F6] p-6">
      <div className="mx-auto">
        {/* Header */}
        <WelcomeHeader 
          userName={data.user.name}
          lastSessionTime={data.user.lastSessionTime}
          avatar={data.user.avatar}
        />

        {/* Juno Intelligence Briefing Quote */}
        <BriefingQuote />

        {/* Main Grid Layout - 4 columns matching Figma exactly */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Column 1 - Activity Feed */}
          <div className="lg:col-span-1">
            <ActivityFeed
              whatsChanged={data.whatsChanged}
              whatNeedsAttention={data.whatNeedsAttention}
              onAttentionItemClick={handleAttentionItemClick}
            />
          </div>

          {/* Column 2 - Key Metrics */}
          <div className="lg:col-span-1 space-y-6">
            {/* Top Metrics Cards */}
            <MetricsCards
              totalOpenSignals={data.totalOpenSignals}
              totalOpenCases={data.totalOpenCases}
            />

            {/* Signals and Cases Over Time Chart */}
            <SignalsTimeChart data={data.signalsAndCasesOverTime} />
          </div>

          {/* Column 3 - Analytics Part 1 */}
          <div className="lg:col-span-1 space-y-6">
            {/* Average Severity Score */}
            <SeverityDonutChart
              averageConductScore={data.averageConductScore}
              strainScore={data.averageStrainScore}
              harmScore={data.averageHarmScore}
            />

            {/* Vulnerability Detection */}
            <VulnerabilityMetric data={data.vulnerabilityDetection} />
          </div>

          {/* Column 4 - Analytics Part 2 */}
          <div className="lg:col-span-1 space-y-6">
            {/* Complaint Detection Chart */}
            <ComplaintDetectionChart data={data.complaintDetection} />

            {/* Channel Breakdown */}
            <ChannelBreakdownChart data={data.channelBreakdown} />

            {/* Removed RootCausesChart */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodayDashboardV4;
